import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FileMeta } from './fileWorkspaceService';
import { prettySize } from './fileWorkspaceService';
import { FilePreviewPane } from './FilePreviewModal';

const ERROR_RED = 'rgba(229,71,77,0.8)';

interface FileWorkspaceDeskProps {
  theme: 'light' | 'dark';
  themeColors: any;
  onClose: () => void;
  usedBytes: number;
  maxBytes: number;
  error: string | null;
  previewFile: FileMeta | null;
  onDownload: (file: FileMeta) => void;
  dragOver: boolean;
  onDragEnter: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  children: React.ReactNode;
}

// Full-screen file desk. The 320px sidebar stays a peek at what this chat
// is using; this surface is for browsing, renaming, and opening files.
export const FileWorkspaceDesk: React.FC<FileWorkspaceDeskProps> = ({
  theme,
  themeColors,
  onClose,
  usedBytes,
  maxBytes,
  error,
  previewFile,
  onDownload,
  dragOver,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
  children,
}) => {
  const c = themeColors[theme];
  const ratio = maxBytes > 0 ? usedBytes / maxBytes : 0;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="File"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        background: theme === 'dark' ? '#0A0B0D' : '#F7F8FA',
        color: c.ink,
        boxShadow: dragOver ? `inset 0 0 0 2px ${c.jade}` : 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          height: '50px',
          minHeight: '50px',
          padding: '0 18px',
          borderBottom: `1px solid ${c.line}`,
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            background: 'none',
            border: 'none',
            padding: 0,
            color: c.ink2,
            fontSize: '13px',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
          Kembali ke chat
        </button>
        <span style={{ fontSize: '13px', fontWeight: 600, color: c.ink }}>File</span>
        {maxBytes > 0 && (
          <span style={{ fontSize: '11px', color: c.ink3, fontFamily: c.mono }}>
            {prettySize(usedBytes)} / {prettySize(maxBytes)}
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={onClose}
            aria-label="Tutup"
            style={{
              width: '30px',
              height: '30px',
              display: 'grid',
              placeItems: 'center',
              background: 'none',
              border: `1px solid ${c.line}`,
              borderRadius: '8px',
              color: c.ink3,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>
      </div>
      {maxBytes > 0 && (
        <div style={{ height: '3px', background: c.line, flexShrink: 0 }}>
          <div
            style={{
              height: '100%',
              width: `${Math.min(100, ratio * 100)}%`,
              background: ratio > 0.9 ? ERROR_RED : c.jade,
            }}
          />
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <div
          className="custom-scrollbar"
          style={{
            width: 'min(400px, 42vw)',
            flexShrink: 0,
            overflowY: 'auto',
            borderRight: `1px solid ${c.line}`,
            padding: '16px 16px 28px',
          }}
        >
          {error && (
            <div style={{ marginBottom: '12px', fontSize: '12px', color: ERROR_RED, lineHeight: 1.4 }}>
              {error}
            </div>
          )}
          {children}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {previewFile ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  height: '46px',
                  padding: '0 18px',
                  borderBottom: `1px solid ${c.line}`,
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: '13.5px',
                    fontWeight: 600,
                    color: c.ink,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={previewFile.filename}
                >
                  {previewFile.filename}
                </div>
                <button
                  type="button"
                  onClick={() => onDownload(previewFile)}
                  style={{
                    height: '30px',
                    padding: '0 12px',
                    borderRadius: '8px',
                    border: `1px solid ${c.line}`,
                    background: 'none',
                    color: c.ink2,
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Unduh
                </button>
              </div>
              <FilePreviewPane
                file={previewFile}
                theme={theme}
                themeColors={themeColors}
                onDownload={onDownload}
              />
            </>
          ) : (
            <div
              style={{
                flex: 1,
                display: 'grid',
                placeItems: 'center',
                padding: '32px',
                color: c.ink3,
                fontSize: '13px',
                textAlign: 'center',
                lineHeight: 1.55,
              }}
            >
              Pilih file di kiri untuk melihat isinya.
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
